import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  hasError?: boolean;
  label?: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ hasError, label, error, hint, className, id, ...rest }, ref) => {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const describedById = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;
    const invalid = hasError || !!error;

    const field = (
      <textarea
        ref={ref}
        id={id ?? (label || describedById ? fieldId : undefined)}
        aria-invalid={invalid || undefined}
        aria-describedby={describedById}
        {...rest}
        className={cn(
          'w-full px-3 py-2 bg-white/70 border border-glass-hairline rounded-glass-sm outline-none',
          'text-label placeholder:text-text-muted text-text-primary resize-y min-h-[5rem]',
          'focus:border-brand-blue/40 focus:bg-white/90',
          invalid && 'border-red-500/50 bg-red-50/50',
          className,
        )}
      />
    );

    // No a11y wrappers requested → render exactly as before.
    if (!label && !error && !hint) return field;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={fieldId} className="text-caption font-medium text-text-secondary">
            {label}
          </label>
        )}
        {field}
        {error ? (
          <span id={`${fieldId}-error`} className="text-caption text-error">
            {error}
          </span>
        ) : hint ? (
          <span id={`${fieldId}-hint`} className="text-caption text-text-muted">
            {hint}
          </span>
        ) : null}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';
